<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settlements.settlements') }}</span>
        <h3 class="page-title">{{ $t('settlements.debtReport.title') }}</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          :disabled="downloadingXlsx"
          @click.prevent="downloadXlsx">
          <template v-if="!downloadingXlsx">{{ $t('settlements.debtReport.downloadXlsx') }}</template>
          <template v-else>
            <div class="spinner-border spinner-border-sm" role="status"></div>
          </template>
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="debt-report-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          type="text"
          v-model="filters.number"
          :placeholder="$t('settlements.debtReport.invoiceNumber')"
          class="w-25 mr-2" />

        <select-customer
          :customerID="filters.customer_id"
          class="w-50 mr-2" />

        <d-button
          theme="info"
          outline
          :class="{
            'text-nowrap': true,
            'ml-auto': true
          }"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
      </validation-observer>
    </d-row>

    <d-card
      style="width: 100%;"
      class="d-flex ml-0 mb-3">
      <d-alert
        :show="!dataLoaded"
        theme="light"
        class="text-center">
        {{ $t('ui.dataLoading') }}...
      </d-alert>
      <table
        v-if="dataLoaded"
        class="table">
        <thead>
          <tr>
            <th>{{ $t('settlements.debtReport.type') }}</th>
            <th>{{ $t('settlements.debtReport.amountNet') }}</th>
            <th>{{ $t('settlements.debtReport.percentage') }}</th>
            <th>{{ $t('settlements.debtReport.count') }}</th>
            <th style="width: 200px;">{{ $t('settlements.debtReport.seeSettlements') }}</th>
          </tr>
        </thead>
        <tfoot>
          <tr>
            <td>
              <strong class="text-primary">
                {{ $t('settlements.debtReport.total') }}
              </strong>
            </td>
            <td colspan="4">
              <strong class="text-primary">
                {{ totalDebt }} PLN
              </strong>
            </td>
          </tr>
        </tfoot>
        <tbody>
          <template v-for="(item, index) of debtItems">
            <tr
              :key="'debt-item-' + index">
              <td>{{ $t('settlements.debtReport.types.' + item.type) }}</td>
              <td>{{ Number(item.debt_amount_net).toFixed(2) }} PLN</td>
              <td>{{ item.percent }}%</td>
              <td>
                <span class="text-primary">
                  {{ item.settlements_count }}
                </span>
              </td>
              <td>
                <d-button
                  @click.prevent="toggleDebtSettlementsVisibility(item.type)"
                  :disabled="!item.settlements_count">
                  <template v-if="debtSettlementsVisible[item.type]">
                    {{ $t('settlements.debtReport.hideSettlements') }}
                  </template>
                  <template v-else>
                    {{ $t('settlements.debtReport.showSettlements') }}
                  </template>
                </d-button>
              </td>
            </tr>
            <tr
              v-show="debtSettlementsVisible[item.type]"
              :key="'debt-item-settlements-' + index">
              <td
                colspan="5"
                class="p-4">
                <d-card>
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>{{ $t('settlements.debtReport.invoiceNumber') }}</th>
                        <th>{{ $t('settlements.debtReport.customer') }}</th>
                        <th>{{ $t('settlements.debtReport.debtOriginal') }}</th>
                        <th>{{ $t('settlements.debtReport.debtCalculated') }}</th>
                        <th>{{ $t('settlements.debtReport.debtPaid') }}</th>
                        <th>{{ $t('settlements.debtReport.dates') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(settlement, subindex) of item.settlements"
                        :key="'debt-item-settlements-' + index + '-' + subindex">
                        <td>
                          <div>
                            {{ settlement.number }}
                          </div>
                          <div
                            v-if="settlement.notes"
                            class="text-light">
                            <small>
                              {{ settlement.notes }}
                            </small>
                          </div>
                          <small class="d-flex">
                            <a
                              v-if="!settlement.notes"
                              href="#add-note"
                              @click.prevent="addNote(settlement.id, item.type)">
                              <i class="material-icons">add</i> {{ $t('settlements.debtReport.addNote') }}
                            </a>
                            <a
                              v-else
                              href="#edit-note"
                              @click.prevent="editNote(settlement.id, settlement.notes, item.type)">
                              <i class="material-icons">edit</i> {{ $t('settlements.debtReport.editNote') }}
                            </a>
                          </small>
                        </td>
                        <td>
                          <small>
                            <strong>{{ settlement.customer_name }}</strong><br>
                            {{ $t('settlements.debtReport.order') }}: <span class="text-primary">{{ settlement.order_name }}</span>
                          </small>
                        </td>
                        <td>
                          {{ settlement.amount_net }} {{ settlement.currency }}
                        </td>
                        <td>
                          {{ settlement.debt_amount_net }} PLN<br>
                          <small>
                            ({{ $t('settlements.debtReport.exchange') }} {{ settlement.debt_exchange_date }}: {{ settlement.debt_exchange_rate }})
                          </small>
                        </td>

                        <td>
                          {{ settlement.amount_paid }} {{ settlement.currency }}<br>
                        </td>
                        <td>
                          <small>
                            {{ $t('settlements.debtReport.invoiceDate') }}:
                            <span class="text-primary">{{ settlement.invoice_date }}</span><br>
                            {{ $t('settlements.debtReport.paymentDate') }}:
                            <span class="text-primary">{{ settlement.payment_date }}</span>
                          </small>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </d-card>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </d-card>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import SelectCustomer from './../components/common/SelectCustomer.vue';
import Vue from 'vue';

export default {
  name: 'debt-report',
  mixins: [
    HasFilters
  ],
  components: {
    'select-customer': SelectCustomer
  },
  computed: {
    totalDebt () {
      return Number(this.debtItems.reduce((prev, next) => prev + next.debt_amount_net, 0)).toFixed(2);
    }
  },
  data () {
    return {
      dataLoaded: false,
      debtItems: [],
      debtSettlementsVisible: {},
      downloadingXlsx: false,
      loadError: false,
      filters: {
        customer_id: null,
        number: ''
      }
    }
  },
  mounted () {
    this.loadFilteredData();
    this.$bus.$on('reload-debt-report', this.debouncedLoadFilteredData);
    this.$bus.$on('ui-select-customer-updated', this.setCustomerID);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let whereData = {};

      if (this.filters.customer_id !== null && this.filters.customer_id > 0) {
        whereData.customer_id = this.filters.customer_id;
      }

      if (this.filters.number !== '') {
        whereData.number = this.filters.number;
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: 'settlements/debt-report/items',
        outputKey: 'debtItems',
        errorKey: 'loadError',
        loadedKey: 'dataLoaded',
        method: 'get',
        params: {
          with: [],
          where: whereData
        },
        successAction: () => {
          for (let i = 0; i < this.debtItems.length; i++) {
            let key = this.debtItems[i].type;

            if (!this.debtSettlementsVisible[key]) {
              this.debtSettlementsVisible[key] = false;
            }
          }
        }
      });
    },
    downloadXlsx () {
      this.downloadingXlsx = true;

      this.$http.post('settlements/debt-report/items', {
        where: {
          download_excel: 1
        }
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.$t('settlements.debtReport.fileName') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingXlsx = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingXlsx = false;
      });
    },
    toggleDebtSettlementsVisibility (debtSettlementsType) {
      let previousValue = this.debtSettlementsVisible[debtSettlementsType];
      Vue.set(this.debtSettlementsVisible, debtSettlementsType, !previousValue);
      this.$forceUpdate();
    },
    addNote (settlementID) {
      Vue.swal({
        input: 'textarea',
        html: this.$t('settlements.debtReport.yourNote'),
        inputPlaceholder: '',
        showCancelButton: true
      }).then((result) => {
        if (result.value) {
          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/settlements/update-notes/' + settlementID,
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('ui.errorOccuredDesc'),
            formData: {
              notes: result.value
            },
            successAction: (response) => {
              if (response.success) {
                this.$bus.$emit('reload-debt-report');
              } else {
                Vue.swal({
                  type: 'warning',
                  title: this.$t('ui.errorOccured'),
                  html: this.$t('ui.errorOccuredDesc'),
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                });
              }
            }
          });
        }
      });
    },
    editNote (settlementID, note) {
      Vue.swal({
        input: 'textarea',
        html: this.$t('settlements.debtReport.yourNote'),
        inputValue: note,
        inputPlaceholder: '',
        showCancelButton: true
      }).then((result) => {
        if (result.value) {
          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/settlements/update-notes/' + settlementID,
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('ui.errorOccuredDesc'),
            formData: {
              notes: result.value
            },
            successAction: (response) => {
              if (response.success) {
                this.$bus.$emit('reload-debt-report');
              } else {
                Vue.swal({
                  type: 'warning',
                  title: this.$t('ui.errorOccured'),
                  html: this.$t('ui.errorOccuredDesc'),
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                });
              }
            }
          });
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        customer_id: null,
        number: ''
      };

      this.$bus.$emit('ui-select-customer-reset');
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      FormUtils.validate(this.$refs['debt-report-filters-form'], this.debouncedLoadFilteredData);
    },
    setCustomerID (customerID) {
      this.filters.customer_id = customerID;
    }
  },
  beforeDestroy () {
    this.$bus.$off('reload-debt-report', this.loadData);
    this.$bus.$off('ui-select-customer-updated', this.setCustomerID);
  }
}
</script>
